import { useEffect, useState } from "react";
import { loadReviewFindingDetails } from "../lib/reviewClient.js";

export function useFullFinding({ findingId, reportId, traceId, contextKey, enabled = true }) {
    const key = enabled && findingId && reportId && traceId ? JSON.stringify([findingId, reportId, traceId, contextKey]) : null;
    const [request, setRequest] = useState(null);
    const [loaded, setLoaded] = useState(null);
    useEffect(() => {
        if (!key || request?.key !== key) return undefined;
        const controller = new AbortController();
        let active = true;
        loadReviewFindingDetails({ traceId, findings: [{ findingId, reportId }], signal: controller.signal }).then(([detail]) => {
            if (active) setLoaded({ request, status: detail.documentExpired ? "expired" : "ready", detail });
        }, () => {
            if (active) setLoaded({ request, status: "error" });
        });
        return () => { active = false; controller.abort(); };
    }, [key, request, findingId, reportId, traceId]);
    const result = key && request?.key === key ? loaded?.request === request ? loaded : { status: "loading" } : { status: "idle" };
    return { ...result, detail: result.status === "ready" ? result.detail : null, more: () => setRequest({ key }) };
}

// Two modes. Without `onToggle`, [more] loads the full text once and the
// control disappears. With `onToggle`, [more]/[less] also shows or hides
// `children` (the verdict text), loading the full text on the first open.
export default function FindingMore({ expansion, expanded = false, onToggle = null, children = null }) {
    const toggle = typeof onToggle === "function";
    if (!toggle && expansion.status === "ready") return null;
    const open = toggle && expanded;
    const loading = expansion.status === "loading", failed = expansion.status === "error";
    // A failed read keeps offering [more] as the retry, whatever is expanded.
    const showLess = open && !failed;
    function click() {
        if (failed) {
            if (toggle) onToggle(true);
            expansion.more();
            return;
        }
        if (toggle) onToggle(!open);
        if (!open && expansion.status === "idle") expansion.more();
    }
    return <div className="my-[8px] text-[12px] leading-[1.5] text-[color:var(--muted)]" aria-busy={loading}>
        {expansion.status === "expired" && !toggle ? <span>The full finding text has expired. Showing the available text.</span> : <>
            <button type="button" className="border-0 bg-transparent p-0 text-[12px] text-[color:var(--green)] hover:underline disabled:opacity-50" aria-label={showLess ? "Show less finding detail" : "Load full finding text"} aria-expanded={toggle ? open : undefined} disabled={loading} onClick={click}>{loading ? "[loading...]" : showLess ? "[less]" : "[more]"}</button>
            {expansion.status === "error" && <span role="alert"> Could not load the full text. Select [more] to try again.</span>}
            {toggle && expansion.status === "expired" && <span> The full finding text has expired. Showing the stored text.</span>}
        </>}
        {open && children}
    </div>;
}
